import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Detective, Globe, Code, Lightning, Plus, X, Lock, Key, FloppyDisk, Trash, ShieldStar } from "@phosphor-icons/react";
import { createRun, listProfiles, createProfile, deleteProfile, getLastPreset } from "@/lib/api";

const glow = "https://images.unsplash.com/photo-1604079628040-94301bb21b91?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGdyYWRpZW50JTIwbWVzaCUyMGJyaWdodHxlbnwwfHx8fDE3ODcwOTE1MDB8MA&ixlib=rb-4.1.0&q=85";

const SAMPLE_SWAGGER = "https://petstore3.swagger.io/api/v3/openapi.json";

export const NewTestForm = ({ onStarted }) => {
  const [mode, setMode] = useState("api"); // api | web
  const [name, setName] = useState("");
  const [source, setSource] = useState("");
  const [specContent, setSpecContent] = useState("");
  const [baseUrl, setBaseUrl] = useState("");
  const [headers, setHeaders] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const [authType, setAuthType] = useState("none");
  const [bearer, setBearer] = useState("");
  const [basicUser, setBasicUser] = useState("");
  const [basicPass, setBasicPass] = useState("");
  const [apiKeyName, setApiKeyName] = useState("");
  const [apiKeyVal, setApiKeyVal] = useState("");
  const [apiKeyIn, setApiKeyIn] = useState("header");

  // OAuth 2.0
  const [oauth, setOauth] = useState({
    token_url: "", client_id: "", client_secret: "", scope: "",
    grant_type: "client_credentials", username: "", password: "", client_auth: "body",
  });
  const setOa = (k, v) => setOauth((o) => ({ ...o, [k]: v }));

  // Presets
  const [profiles, setProfiles] = useState([]);
  const [selectedProfile, setSelectedProfile] = useState("");
  const [presetName, setPresetName] = useState("");
  const [savingPreset, setSavingPreset] = useState(false);

  const loadProfiles = async () => {
    try { setProfiles(await listProfiles()); } catch { /* ignore */ }
  };
  useEffect(() => { loadProfiles(); }, []);

  const buildAuthConfig = () => {
    if (authType === "bearer" && bearer) return { type: "bearer", token: bearer };
    if (authType === "basic" && (basicUser || basicPass)) return { type: "basic", username: basicUser, password: basicPass };
    if (authType === "apikey" && apiKeyName) return { type: "apikey", key: apiKeyName, value: apiKeyVal, add_to: apiKeyIn };
    if (authType === "oauth2" && oauth.token_url) return { type: "oauth2", ...oauth };
    return undefined;
  };

  const applyAuthConfig = (cfg) => {
    if (!cfg || !cfg.type) { setAuthType("none"); return; }
    setAuthType(cfg.type);
    if (cfg.type === "bearer") setBearer(cfg.token || "");
    if (cfg.type === "basic") { setBasicUser(cfg.username || ""); setBasicPass(cfg.password || ""); }
    if (cfg.type === "apikey") { setApiKeyName(cfg.key || ""); setApiKeyVal(cfg.value || ""); setApiKeyIn(cfg.add_to || "header"); }
    if (cfg.type === "oauth2") setOauth({
      token_url: cfg.token_url || "", client_id: cfg.client_id || "", client_secret: cfg.client_secret || "",
      scope: cfg.scope || "", grant_type: cfg.grant_type || "client_credentials",
      username: cfg.username || "", password: cfg.password || "", client_auth: cfg.client_auth || "body",
    });
  };

  const applyPreset = (config) => {
    if (!config) return;
    const wrapped = config.auth !== undefined || config.base_url !== undefined;
    const authCfg = wrapped ? config.auth : config;
    applyAuthConfig(authCfg);
    if (wrapped && config.base_url !== undefined) setBaseUrl(config.base_url || "");
  };

  const presetLabel = (p) => {
    const t = p.config?.auth?.type || p.config?.type || "none";
    const bu = p.config?.base_url;
    let host = "";
    if (bu) { try { host = new URL(bu).host; } catch { host = bu; } }
    return `${p.name} · ${t}${host ? " · " + host : ""}`;
  };

  const onSelectProfile = (id) => {
    setSelectedProfile(id);
    const p = profiles.find((x) => x.id === id);
    if (p) { applyPreset(p.config); toast.success(`Loaded preset "${p.name}"`); }
  };

  const savePreset = async () => {
    const cfg = buildAuthConfig();
    if (!cfg && !baseUrl.trim()) { toast.error("Configure authorization or a base URL before saving."); return; }
    if (!presetName.trim()) { toast.error("Give the preset a name."); return; }
    setSavingPreset(true);
    try {
      const config = { auth: cfg || { type: "none" }, base_url: baseUrl.trim() };
      const p = await createProfile({ name: presetName.trim(), config });
      toast.success(`Preset "${p.name}" saved`);
      setPresetName("");
      await loadProfiles();
      setSelectedProfile(p.id);
    } catch { toast.error("Failed to save preset."); }
    finally { setSavingPreset(false); }
  };

  const removePreset = async () => {
    if (!selectedProfile) return;
    await deleteProfile(selectedProfile);
    toast.success("Preset deleted");
    setSelectedProfile("");
    await loadProfiles();
  };

  // Preset Autoload: when a target is entered, pre-select the preset last used for it.
  const onSourceBlur = async () => {
    if (!source || selectedProfile) return;
    try {
      const res = await getLastPreset(source);
      if (res?.profile_id) {
        const p = profiles.find((x) => x.id === res.profile_id);
        if (p) {
          applyPreset(p.config);
          setSelectedProfile(p.id);
          toast.success(`Auto-loaded preset "${p.name}" used last time for this target`);
        }
      }
    } catch { /* ignore */ }
  };

  const addHeader = () => setHeaders((h) => [...h, { k: "", v: "" }]);
  const setHeader = (i, key, val) =>
    setHeaders((h) => h.map((x, idx) => (idx === i ? { ...x, [key]: val } : x)));
  const removeHeader = (i) => setHeaders((h) => h.filter((_, idx) => idx !== i));

  const submit = async () => {
    if (mode === "api" && !source && !specContent) {
      toast.error("Provide a Swagger/OpenAPI URL or paste the spec.");
      return;
    }
    if (mode === "web" && !source) {
      toast.error("Enter the web application URL.");
      return;
    }
    const auth = {};
    headers.forEach((h) => { if (h.k.trim()) auth[h.k.trim()] = h.v; });

    const authConfig = buildAuthConfig();

    setSubmitting(true);
    try {
      const run = await createRun({
        name: name || undefined,
        target_type: mode,
        source: source || undefined,
        spec_content: mode === "api" && specContent ? specContent : undefined,
        base_url: baseUrl || undefined,
        auth_headers: Object.keys(auth).length ? auth : undefined,
        auth: authConfig,
        profile_id: selectedProfile || undefined,
      });
      toast.success("Agent dispatched — self-learning your target.");
      onStarted(run.id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start test run.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="mx-auto max-w-3xl">
      {/* Hero */}
      <div className="relative mb-8 overflow-hidden rounded-3xl border border-border bg-card p-8 lg:p-10">
        <img src={glow} alt="" className="pointer-events-none absolute -right-16 -top-16 h-64 w-64 rounded-full opacity-40 blur-2xl" />
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <span className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
            <Detective weight="fill" size={14} /> Zero human intervention
          </span>
          <h1 className="font-head text-4xl font-extrabold tracking-tight sm:text-5xl">
            Point the agent at anything.
          </h1>
          <p className="mt-3 max-w-xl text-base text-muted-foreground">
            Sentinel Qi learns your API from its Swagger spec — or crawls your live web app — designs its own test suite, runs it, and hands you a detailed report.
          </p>
        </motion.div>
      </div>

      {/* Mode switch */}
      <div className="mb-6 grid grid-cols-2 gap-3">
        {[
          { id: "api", icon: Code, title: "REST API", sub: "via Swagger / OpenAPI" },
          { id: "web", icon: Globe, title: "Web App", sub: "via live URL crawl" },
        ].map((m) => (
          <button
            key={m.id}
            data-testid={`mode-${m.id}`}
            onClick={() => setMode(m.id)}
            className={`flex items-center gap-3 rounded-2xl border p-4 text-left transition-all ${
              mode === m.id
                ? "border-primary bg-primary/5 shadow-md shadow-primary/10"
                : "border-border bg-card hover:border-muted-foreground/40"
            }`}
          >
            <div className={`flex h-11 w-11 items-center justify-center rounded-xl ${mode === m.id ? "bg-primary text-primary-foreground" : "bg-muted text-muted-foreground"}`}>
              <m.icon size={22} weight="bold" />
            </div>
            <div>
              <div className="font-head font-semibold">{m.title}</div>
              <div className="text-xs text-muted-foreground">{m.sub}</div>
            </div>
          </button>
        ))}
      </div>

      {/* Form */}
      <div className="space-y-5 rounded-2xl border border-border bg-card p-6 lg:p-8">
        <Field label="Run name (optional)">
          <input data-testid="input-name" value={name} onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Nightly regression" className={inputCls} />
        </Field>

        <Field label={mode === "api" ? "Swagger / OpenAPI URL" : "Web application URL"}>
          <div className="flex gap-2">
            <input data-testid="input-source" value={source} onChange={(e) => setSource(e.target.value)}
              onBlur={onSourceBlur}
              placeholder={mode === "api" ? "https://api.example.com/openapi.json" : "https://example.com"}
              className={`${inputCls} font-mono text-sm`} />
            {mode === "api" && (
              <button data-testid="use-sample" onClick={() => setSource(SAMPLE_SWAGGER)}
                className="shrink-0 rounded-xl border border-border px-3 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent">
                Sample
              </button>
            )}
          </div>
        </Field>

        {mode === "api" && (
          <>
            <Field label="Base URL (saved with preset · optional)">
              <input data-testid="input-baseurl" value={baseUrl} onChange={(e) => setBaseUrl(e.target.value)}
                placeholder="https://staging.api.example.com" className={`${inputCls} font-mono text-sm`} />
            </Field>
            <Field label="Or paste the raw spec (JSON/YAML)">
              <textarea data-testid="input-spec" value={specContent} onChange={(e) => setSpecContent(e.target.value)}
                rows={4} placeholder='{ "openapi": "3.0.0", ... }'
                className={`${inputCls} resize-y font-mono text-xs`} />
            </Field>
          </>
        )}

        {/* Authorization (Postman-style) */}
        <div className="rounded-xl border border-border bg-background/50 p-4">
          <div className="mb-3 flex items-center gap-2">
            <Lock size={16} weight="bold" className="text-primary" />
            <label className="text-sm font-semibold text-foreground">Authorization</label>
          </div>

          {/* Preset bar */}
          <div className="mb-4 flex flex-wrap items-center gap-2 rounded-lg border border-dashed border-border bg-card/60 p-2.5">
            <ShieldStar size={16} weight="fill" className="text-primary" />
            <select
              data-testid="preset-select"
              value={selectedProfile}
              onChange={(e) => onSelectProfile(e.target.value)}
              className="flex-1 min-w-[140px] rounded-lg border border-input bg-background px-2.5 py-1.5 text-xs text-foreground outline-none focus:border-primary"
            >
              <option value="">Load a saved preset…</option>
              {profiles.map((p) => (
                <option key={p.id} value={p.id}>{presetLabel(p)}</option>
              ))}
            </select>
            {selectedProfile && (
              <button data-testid="preset-delete" onClick={removePreset}
                className="rounded-lg border border-border px-2 py-1.5 text-muted-foreground transition-colors hover:bg-accent hover:text-[hsl(0_75%_58%)]">
                <Trash size={14} />
              </button>
            )}
            <input
              data-testid="preset-name"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder="New preset name"
              className="w-32 rounded-lg border border-input bg-background px-2.5 py-1.5 text-xs text-foreground outline-none focus:border-primary"
            />
            <button data-testid="preset-save" disabled={savingPreset} onClick={savePreset}
              className="inline-flex items-center gap-1 rounded-lg bg-primary px-2.5 py-1.5 text-xs font-semibold text-primary-foreground transition-all hover:brightness-110 disabled:opacity-60">
              <FloppyDisk size={14} weight="bold" /> Save
            </button>
          </div>

          <div className="mb-4 flex flex-wrap gap-2">
            {[
              { id: "none", label: "No Auth" },
              { id: "bearer", label: "Bearer Token" },
              { id: "basic", label: "Basic Auth" },
              { id: "apikey", label: "API Key" },
              { id: "oauth2", label: "OAuth 2.0" },
            ].map((a) => (
              <button
                key={a.id}
                data-testid={`auth-${a.id}`}
                onClick={() => setAuthType(a.id)}
                className={`rounded-full border px-3.5 py-1.5 text-xs font-medium transition-colors ${
                  authType === a.id
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-card text-muted-foreground hover:bg-accent"
                }`}
              >
                {a.label}
              </button>
            ))}
          </div>

          {authType === "bearer" && (
            <input data-testid="auth-bearer-token" value={bearer} onChange={(e) => setBearer(e.target.value)}
              placeholder="Token (e.g. eyJhbGci...)" className={`${inputCls} font-mono text-xs`} />
          )}
          {authType === "basic" && (
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="auth-basic-user" value={basicUser} onChange={(e) => setBasicUser(e.target.value)}
                placeholder="Username" className={`${inputCls} font-mono text-xs`} />
              <input data-testid="auth-basic-pass" type="password" value={basicPass} onChange={(e) => setBasicPass(e.target.value)}
                placeholder="Password" className={`${inputCls} font-mono text-xs`} />
            </div>
          )}
          {authType === "apikey" && (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="auth-apikey-name" value={apiKeyName} onChange={(e) => setApiKeyName(e.target.value)}
                  placeholder="Key (e.g. X-API-Key)" className={`${inputCls} font-mono text-xs`} />
                <input data-testid="auth-apikey-value" value={apiKeyVal} onChange={(e) => setApiKeyVal(e.target.value)}
                  placeholder="Value" className={`${inputCls} font-mono text-xs`} />
              </div>
              <div className="flex items-center gap-2">
                <Key size={14} className="text-muted-foreground" />
                <span className="text-xs text-muted-foreground">Add to</span>
                <div className="flex gap-1 rounded-lg bg-muted p-1">
                  {["header", "query"].map((loc) => (
                    <button key={loc} data-testid={`auth-apikey-in-${loc}`} onClick={() => setApiKeyIn(loc)}
                      className={`rounded-md px-3 py-1 text-xs font-medium capitalize transition-colors ${
                        apiKeyIn === loc ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
                      {loc === "query" ? "Query Param" : "Header"}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}
          {authType === "oauth2" && (
            <div className="space-y-2">
              <input data-testid="oauth-token-url" value={oauth.token_url} onChange={(e) => setOa("token_url", e.target.value)}
                placeholder="Token URL (e.g. https://auth.example.com/oauth/token)" className={`${inputCls} font-mono text-xs`} />
              <div className="grid grid-cols-2 gap-2">
                <input data-testid="oauth-client-id" value={oauth.client_id} onChange={(e) => setOa("client_id", e.target.value)}
                  placeholder="Client ID" className={`${inputCls} font-mono text-xs`} />
                <input data-testid="oauth-client-secret" type="password" value={oauth.client_secret} onChange={(e) => setOa("client_secret", e.target.value)}
                  placeholder="Client Secret" className={`${inputCls} font-mono text-xs`} />
              </div>
              <input data-testid="oauth-scope" value={oauth.scope} onChange={(e) => setOa("scope", e.target.value)}
                placeholder="Scope (optional, e.g. read write)" className={`${inputCls} font-mono text-xs`} />
              <div className="flex flex-wrap items-center gap-3">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Grant</span>
                  <div className="flex gap-1 rounded-lg bg-muted p-1">
                    {["client_credentials", "password"].map((g) => (
                      <button key={g} data-testid={`oauth-grant-${g}`} onClick={() => setOa("grant_type", g)}
                        className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                          oauth.grant_type === g ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
                        {g === "client_credentials" ? "Client Credentials" : "Password"}
                      </button>
                    ))}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground">Send client</span>
                  <div className="flex gap-1 rounded-lg bg-muted p-1">
                    {["body", "basic"].map((c) => (
                      <button key={c} data-testid={`oauth-clientauth-${c}`} onClick={() => setOa("client_auth", c)}
                        className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                          oauth.client_auth === c ? "bg-card text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground"}`}>
                        {c === "body" ? "In Body" : "Basic Header"}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
              {oauth.grant_type === "password" && (
                <div className="grid grid-cols-2 gap-2">
                  <input data-testid="oauth-username" value={oauth.username} onChange={(e) => setOa("username", e.target.value)}
                    placeholder="Username" className={`${inputCls} font-mono text-xs`} />
                  <input data-testid="oauth-password" type="password" value={oauth.password} onChange={(e) => setOa("password", e.target.value)}
                    placeholder="Password" className={`${inputCls} font-mono text-xs`} />
                </div>
              )}
              <p className="text-[11px] text-muted-foreground">
                Sentinel fetches an access token from the token endpoint automatically before testing — no manual token pasting.
              </p>
            </div>
          )}
          <p className="mt-3 text-[11px] text-muted-foreground">
            Applied to spec fetching and every generated test request — just like Postman.
          </p>
        </div>

        {/* Custom headers */}
        <div>
          <div className="mb-2 flex items-center justify-between">
            <label className="text-sm font-medium text-foreground">Custom headers (optional)</label>
            <button data-testid="add-header" onClick={addHeader}
              className="inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline">
              <Plus size={14} weight="bold" /> Add header
            </button>
          </div>
          <div className="space-y-2">
            {headers.map((h, i) => (
              <div key={i} className="flex gap-2">
                <input value={h.k} onChange={(e) => setHeader(i, "k", e.target.value)}
                  placeholder="Authorization" className={`${inputCls} font-mono text-xs`} />
                <input value={h.v} onChange={(e) => setHeader(i, "v", e.target.value)}
                  placeholder="Bearer ..." className={`${inputCls} font-mono text-xs`} />
                <button onClick={() => removeHeader(i)}
                  className="shrink-0 rounded-xl border border-border px-2 text-muted-foreground hover:bg-accent">
                  <X size={16} />
                </button>
              </div>
            ))}
          </div>
        </div>

        <button
          data-testid="start-test-btn"
          disabled={submitting}
          onClick={submit}
          className="group flex w-full items-center justify-center gap-2 rounded-xl bg-primary py-3.5 font-head font-semibold text-primary-foreground shadow-lg shadow-primary/30 transition-all hover:shadow-xl hover:shadow-primary/40 hover:brightness-110 disabled:opacity-60 focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <Lightning weight="fill" size={20} className="transition-transform group-hover:scale-110" />
          {submitting ? "Dispatching agent…" : "Launch autonomous test"}
        </button>
      </div>
    </div>
  );
};

const inputCls =
  "w-full rounded-xl border-2 border-input bg-background px-3.5 py-2.5 text-foreground outline-none transition-colors placeholder:text-muted-foreground/60 focus:border-primary";

const Field = ({ label, children }) => (
  <div>
    <label className="mb-2 block text-sm font-medium text-foreground">{label}</label>
    {children}
  </div>
);
